import React, { FC, useCallback, useEffect, useMemo } from 'react';
import { observer } from 'mobx-react';
import { Button, Image, Tag, Tooltip } from 'antd';
import { CheckCircleFilled, ExclamationCircleOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { CarouselRef } from 'antd/es/carousel';

import { useStores } from '@/hooks/use-stores';
import { Image as ImageData } from '@/store/ImageUpload/ImageUploadStore';
import { ImagePreviewCarousel } from '@/comp/Images/ImagePreviewCarousel/ImagePreviewCarousel';
import { ImagePreviewTagHeader } from '@/comp/Images/ImagePreviewTagHeader/ImagePreviewTagHeader';
import TrashSecondaryIcon from '@/comp/common/icons/TrashSecondaryIcon';
import { useImageCompression } from '@/comp/Forms/SiteSurveyForm/RoomConfigurationStep/FormComponents/ImageUpload/hooks/useImageCompression';

import { StyledImageTagging } from './ImageTagging.styles';
import { ImageUploadButtons } from '../ImageUploadButtons';
import {
  ImageTags,
  COMPRESSOR_IMAGE_TAG_DIRECTORY_DATA,
  getImageTagDataByDeviceType,
} from '@/constants/CompressorImageTagData.const';

type Props = {
  onComplete: () => void;
  layout: 'desktop' | 'mobile';
  loading?: boolean;
};

export const ImageTagging: FC<Props> = observer(({ onComplete, layout, loading }) => {
  const { t } = useTranslation();
  const { imageUploadStore, mapBuilderStore } = useStores();
  const { images, removeImage, goToFirstStep, addTag, removeTag, maxFiles } = imageUploadStore;
  const [showErrors, setShowErrors] = React.useState(false);
  const carouselRef = React.useRef<CarouselRef>(null);
  const thumbnailListRef = React.useRef<HTMLDivElement>(null);
  const [selectedIndex, setSelectedIndex] = React.useState(0);
  const [previewImageIndex, setPreviewImageIndex] = React.useState(0);

  const selectedImage = useMemo(() => images[selectedIndex], [images, selectedIndex]);

  useEffect(() => {
    if (selectedIndex < 0) return;

    const thumbnailList = thumbnailListRef.current;

    if (!thumbnailList) return;

    thumbnailList.children[selectedIndex]?.scrollIntoView?.({
      behavior: 'smooth',
      block: 'nearest',
    });
  }, [selectedIndex]);

  useEffect(() => {
    if (!images.length) {
      goToFirstStep();
      return;
    }

    const index = selectedIndex >= images.length ? images.length - 1 : selectedIndex;
    selectImage(index);
  }, [images]);

  const selectImage = (index: number): void => {
    setTimeout(() => {
      if (carouselRef.current) {
        carouselRef.current.goTo(index);
      }
    }, 0);
  };

  const handleComplete = () => selectImage(images.length - 1);

  const params = useImageCompression({ onComplete: handleComplete });

  const removeCurrentImage = (): void => removeImage(selectedImage.id);

  const handleTagClick = (tag: ImageTags) => {
    const selectedImageId = images[selectedIndex].id;

    if (images[selectedIndex].tag === tag) {
      removeTag(selectedImageId);
    } else {
      addTag(selectedImageId, tag);
    }
  };

  const handleSubmit = () => {
    const imageErrorIndex = images.findIndex(image => !image.tag);

    if (imageErrorIndex !== -1) {
      setShowErrors(true);

      if (selectedImage.tag) {
        selectImage(imageErrorIndex);
      }

      return;
    }

    onComplete();
  };

  const getImageThumbnaleClass = useCallback(
    (image: ImageData) =>
      classNames('image-thumbnale', {
        'image-thumbnale-selected': selectedImage?.id === image.id,
        'image-thumbnale-error': showErrors && !image.tag,
      }),
    [selectedIndex, showErrors, images],
  );

  const getImageErrorMessageClass = () =>
    classNames('image-error-message-container', {
      hidden: !showErrors || images.every(({ tag }) => tag),
    });

  const handleBackToUpload = () => {
    params.reset();
    goToFirstStep();
  };

  return (
    <StyledImageTagging className={`image-tagging-container-${layout}`}>
      <p className="image-tagging-helper-text">{t('uploadImageImageTaggingHelperText')}</p>
      <div className="image-thumbnale-list" ref={thumbnailListRef}>
        {images.map((image, index) => (
          <div key={image.id} className="image-thumbnale-container" onClick={() => selectImage(index)}>
            <div className="image-thumbnale">
              <Image className={getImageThumbnaleClass(image)} src={image.url} preview={false} />
              {image.tag && <CheckCircleFilled className="image-tag-checkmark" />}
            </div>
            {layout === 'desktop' && image.tag && (
              <Tooltip title={t(COMPRESSOR_IMAGE_TAG_DIRECTORY_DATA[image.tag].translationKey)}>
                <p className="image-tagging-desktop-thumbnale-tag">
                  {t(COMPRESSOR_IMAGE_TAG_DIRECTORY_DATA[image.tag].translationKey)}
                </p>
              </Tooltip>
            )}
          </div>
        ))}
      </div>
      <div className={getImageErrorMessageClass()}>
        <ExclamationCircleOutlined className="error-icon" />
        <p className="error-text">{t('uploadImageTaggingError')}</p>
      </div>
      <div className="image-info">
        <span className="image-info-title">Picture</span>
        <span className="image-info-title">
          {selectedIndex + 1}/{images.length}
        </span>
        <Tooltip title={selectedImage?.file.name}>
          <span className="image-info-name">{selectedImage?.file.name}</span>
        </Tooltip>
        <Button
          className="remove-image-btn"
          type="text"
          icon={<TrashSecondaryIcon />}
          onClick={removeCurrentImage}
          disabled={params.isLoading}
        />
      </div>
      <ImagePreviewCarousel
        carouselRef={carouselRef}
        className="image-carousel"
        size={layout === 'mobile' ? 'small' : 'medium'}
        selectedIndex={selectedIndex}
        onSelectedIndexChange={setSelectedIndex}
        images={images.map(({ file, ...image }) => ({ ...image, name: file.name }))}
        title={<ImagePreviewTagHeader filename={images[previewImageIndex]?.file.name} {...images[previewImageIndex]} />}
        currentPreviewIndex={previewImageIndex}
        setCurrentPreviewIndex={setPreviewImageIndex}
      />
      <div className="image-tag-list-container">
        <div className="image-tag-list">
          {getImageTagDataByDeviceType(mapBuilderStore.selectedDevice?.deviceType).map(({ tag, translationKey }) => (
            <Tag.CheckableTag
              key={tag}
              className="image-tag"
              checked={selectedImage?.tag == tag}
              onChange={() => handleTagClick(tag)}
            >
              {t(translationKey)}
            </Tag.CheckableTag>
          ))}
        </div>
      </div>
      <div className="image-tagging-footer">
        {images.length < maxFiles && (
          <>
            {layout === 'mobile' ? (
              <ImageUploadButtons buttonType="text" buttonText="uploadImageUnifiedButtonAddAnotherText" {...params} />
            ) : (
              <Button className="image-upload-btn" onClick={handleBackToUpload}>
                {t('uploadImageBackToUpload')}
              </Button>
            )}
          </>
        )}
        <Button
          className="image-upload-btn image-upload-finish"
          size={layout === 'mobile' ? 'large' : 'middle'}
          type="primary"
          disabled={!images.length || params.isLoading}
          loading={loading || params.isLoading}
          onClick={handleSubmit}
        >
          {t('finish')}
        </Button>
      </div>
    </StyledImageTagging>
  );
});

export default ImageTagging;
